/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IButtonProps, IButtonStyles, IconButton } from '@fluentui/react/lib/Button';
import { Icon } from '@fluentui/react/lib/Icon';
import { Separator } from '@fluentui/react/lib/Separator';
import { Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useHistory } from 'react-router';
import { EditableTitle } from 'src/core/components/EditableTitle';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { globalMessages } from 'src/core/utils/globalMessages';
import { routeTable } from 'src/core/utils/routeTable';
import { DemoCommandBar, DemoCommandBarProps } from 'src/languageStudio/appShell/prebuiltDemos/components/DemoCommandBar';
import { commonDemosMessages } from 'src/languageStudio/appShell/prebuiltDemos/utils/commonDemoMessages';
import { headerClassName } from 'src/languageStudio/appShell/prebuiltDemos/utils/commonStyles';

export type DemoHeaderProps = DemoCommandBarProps & {
    demoTitle: string;
    featureName: string;
    demoVersion?: string;
    learnMoreLink?: string;
    secondaryText?: string;
    demoDescription: React.ReactNode;
    supportedPlatforms?: ('cloud' | 'docker')[];
    onRenderHeaderSideInformation?: () => React.ReactNode;
};
const messages = defineMessages({
    back: { id: 'languageStudio.demoHeader.back', defaultMessage: 'Go back to home' },
    version: { id: 'languageStudio.demoHeader.version', defaultMessage: 'Version {version}' }
});

const theme = getScopedTheme('DemoHeader');
const closeButtonStyles = classNamesFunction<IButtonProps, IButtonStyles>()({ root: { color: theme.closeIconColor } });

export const DemoHeader = (props: DemoHeaderProps) => {
    const {
        demoTitle,
        featureName,
        demoVersion,
        learnMoreLink,
        secondaryText,
        demoDescription,
        supportedPlatforms,
        onRenderHeaderSideInformation,
        ...commandBarProps
    } = props;
    const history = useHistory();
    const { localize } = useLocalization();
    const { isMediumOrGreater } = useResponsiveMode();

    return (
        <Stack tokens={{ childrenGap: 20 }}>
            <Stack>
                <Stack horizontal horizontalAlign="space-between">
                    {secondaryText ? (
                        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 10 }}>
                            <EditableTitle title={demoTitle} />
                            <LuisText styles={{ root: { fontStyle: 'italic' } }}>{secondaryText}</LuisText>
                        </Stack>
                    ) : (
                        <EditableTitle className={headerClassName} titleTextProps={{ fontWeight: 500 }} title={demoTitle} />
                    )}
                    <IconButton
                        styles={closeButtonStyles}
                        ariaLabel={localize(messages.back)}
                        iconProps={{ iconName: 'ChromeClose' }}
                        onClick={() => history.push(routeTable.languageStudio.home())}
                    />
                </Stack>
                {demoVersion && (
                    <LuisText variant="small" color={theme.secondaryTextColor}>
                        {localize(messages.version, { version: demoVersion })}
                    </LuisText>
                )}
            </Stack>

            <Stack tokens={{ childrenGap: 14 }}>
                <Stack horizontal={isMediumOrGreater} tokens={{ childrenGap: 53 }}>
                    <LuisText>
                        {demoDescription}
                        {learnMoreLink && <LuisText>&nbsp;</LuisText>}
                        {learnMoreLink && <ExternalLink href={learnMoreLink}>{localize(globalMessages.learnMore)}</ExternalLink>}
                    </LuisText>

                    <Stack horizontal={isMediumOrGreater} tokens={{ childrenGap: 20 }}>
                        <Separator vertical={isMediumOrGreater} />

                        <Stack grow tokens={{ padding: '0 4px 0 0' }}>
                            {onRenderHeaderSideInformation
                                ? onRenderHeaderSideInformation()
                                : [
                                      <LuisText fontWeight={500}>{localize(commonDemosMessages.platforms)}</LuisText>,
                                      <Stack horizontal={isMediumOrGreater} wrap={false} tokens={{ childrenGap: 10 }}>
                                          {supportedPlatforms.some(p => p === 'docker') && (
                                              <Stack horizontal tokens={{ childrenGap: 7 }} verticalAlign="end">
                                                  <Icon
                                                      iconName="DockerLogo"
                                                      styles={{ root: { fontFamily: 'FabricMDL2Icons-0', opacity: 0.5 } }}
                                                  />
                                                  <LuisText nowrap>{localize(commonDemosMessages.dockerContainer)}</LuisText>
                                              </Stack>
                                          )}

                                          {supportedPlatforms.some(p => p === 'cloud') && (
                                              <Stack horizontal tokens={{ childrenGap: 7 }} verticalAlign="end">
                                                  <Icon iconName="Cloud" />
                                                  <LuisText nowrap>{localize(commonDemosMessages.cloud)}</LuisText>
                                              </Stack>
                                          )}
                                      </Stack>
                                  ]}
                        </Stack>
                    </Stack>
                </Stack>

                <DemoCommandBar featureName={featureName} {...commandBarProps} />
            </Stack>
        </Stack>
    );
};
